import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "../i18n";
import { pathFor } from "../config/site";
import LanguageSwitcher from "./LanguageSwitcher";

const NAV = ["home", "timeline", "music", "film", "style", "legacy", "about", "contact"];

export default function Header({ pageKey }) {
  const { lang, d } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`relative z-30 transition-colors duration-300 ${
        scrolled ? "bg-jlo-bg/90 backdrop-blur-md border-b border-jlo-gold/15" : "bg-jlo-bg/70 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link
            to={pathFor(lang, "home")}
            data-testid="logo-link"
            className="focus-gold flex items-center gap-2 shrink-0"
            aria-label="Independent Jennifer Lopez Tribute — Home"
          >
            <span className="font-display text-xl sm:text-2xl font-bold tracking-tight">
              <span className="gold-text">JLo</span>
              <span className="text-jlo-warm"> Timeline</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV.map((key) => (
              <Link
                key={key}
                to={pathFor(lang, key)}
                data-testid={`nav-${key}`}
                className={`px-3 py-2 text-sm rounded-md transition-colors ${
                  pageKey === key
                    ? "text-jlo-gold"
                    : "text-jlo-warm/80 hover:text-jlo-gold"
                }`}
              >
                {d.ui.nav[key]}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <LanguageSwitcher pageKey={pageKey} />
            <button
              data-testid="mobile-menu-toggle"
              className="focus-gold lg:hidden inline-flex items-center justify-center rounded-md p-2 text-jlo-warm hover:text-jlo-gold"
              aria-label="Open menu"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-jlo-bg2 border-t border-jlo-gold/15">
          <nav className="px-4 py-3 grid gap-1" aria-label="Mobile">
            {NAV.map((key) => (
              <Link
                key={key}
                to={pathFor(lang, key)}
                data-testid={`mobile-nav-${key}`}
                onClick={() => setOpen(false)}
                className={`px-3 py-2.5 rounded-md text-base ${
                  pageKey === key ? "text-jlo-gold bg-jlo-gold/10" : "text-jlo-warm/85"
                }`}
              >
                {d.ui.nav[key]}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
